import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { users as usersApi } from '../../services/api';
import LayoutV2 from '../../components/LayoutV2';
import config from '../../config';
import {
  Button,
  Loading,
  PageHeader,
  SectionBlock
} from '../../components/ui';

function generatePreviewPassword(length = 6) {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const chars = `${letters}${digits}`;
  let value = letters.charAt(Math.floor(Math.random() * letters.length));
  value += digits.charAt(Math.floor(Math.random() * digits.length));
  while (value.length < length) {
    value += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return value
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('');
}

function buildLoginLink() {
  if (typeof window === 'undefined') {
    return config.publicUrl || '/';
  }
  const publicPath = (config.publicUrl || '').replace(/\/$/, '');
  return `${window.location.origin}${publicPath || ''}`;
}

function buildOnboardingPackage(form, parents) {
  const parent = parents.find((item) => item.id === form.parentId);
  const loginLink = buildLoginLink();
  const packageData = {
    username: String(form.username || '').trim(),
    password: String(form.password || '').trim(),
    loginLink,
    upline: parent
      ? {
          id: parent.id,
          username: parent.username,
          displayName: parent.display_name || parent.displayName
        }
      : null
  };

  const uplineLine = packageData.upline
    ? `${packageData.upline.displayName} (@${packageData.upline.username})`
    : 'No upline';

  return {
    ...packageData,
    text: [
      'Your Octopus account is ready.',
      `Username: ${packageData.username}`,
      `Password: ${packageData.password}`,
      `Login Link: ${packageData.loginLink}`,
      `Upline: ${uplineLine}`
    ].join('\n')
  };
}

export default function UsersV2() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [search, setSearch] = useState({ keyword: '', tiers: [] });
  const [showForm, setShowForm] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [createdPackage, setCreatedPackage] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [previewCopyFeedback, setPreviewCopyFeedback] = useState('');
  const [createdCopyFeedback, setCreatedCopyFeedback] = useState('');
  const [form, setForm] = useState({ username: '', password: generatePreviewPassword(), displayName: '', tier: 1, parentId: '', region: '' });
  const [editForm, setEditForm] = useState({ username: '', displayName: '', tier: '', parentId: '', region: '', status: '' });
  const [parents, setParents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const loadParents = async () => {
      try {
        const res = await usersApi.list();
        setParents(res.data.users || []);
      } catch (err) {
        console.error(err);
      }
    };

    loadParents();
  }, []);

  const loadUsers = async (override = null) => {
    const activeSearch = override || search;
    const params = {};
    const keyword = String(activeSearch.keyword || '').trim();
    if (keyword) params.keyword = keyword;
    if (activeSearch.tiers.length > 0) params.tiers = activeSearch.tiers.join(',');

    setLoading(true);
    try {
      const res = await usersApi.list(params);
      setList(res.data.users || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const password = String(form.password || '').trim() || generatePreviewPassword();
      const payload = {
        ...form,
        password,
        appBaseUrl: buildLoginLink()
      };
      const res = await usersApi.create(payload);
      setCreatedPackage(res.data.onboardingPackage || buildOnboardingPackage({ ...form, password }, parents));
      setShowForm(false);
      setForm({ username: '', password: generatePreviewPassword(), displayName: '', tier: 1, parentId: '', region: '' });
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await usersApi.update(showEdit, editForm);
      setShowEdit(null);
      if (hasSearched) await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update user');
    }
  };

  const openEdit = (user) => {
    setEditForm({
      username: user.username,
      displayName: user.display_name,
      tier: user.tier,
      parentId: user.parent_id || '',
      region: user.region || '',
      status: user.status
    });
    setShowEdit(user.id);
  };

  const handleDisable = async (id) => {
    const user = list.find((u) => u.id === id);
    if (!user || user.status === 'disabled') return;
    if (!window.confirm(`Disable user "${user.display_name}"?`)) return;
    try {
      await usersApi.disable(id);
      if (hasSearched) await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to disable user');
    }
  };

  const handleResetPassword = async (id) => {
    const newPassword = prompt('Enter new password (min 6 characters):');
    if (!newPassword) return;
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      await usersApi.resetPassword(id, newPassword);
      alert('Password reset successfully');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const tierNames = { 0: 'Admin', 1: 'Director', 2: 'Manager', 3: 'Promoter', 4: 'Operations' };
  const searchableTiers = [
    { value: 1, label: 'Director' },
    { value: 2, label: 'Manager' },
    { value: 3, label: 'Promoter' },
    { value: 4, label: 'Operations' }
  ];

  const toggleTier = (tierValue) => {
    setSearch((prev) => {
      const hasTier = prev.tiers.includes(tierValue);
      return {
        ...prev,
        tiers: hasTier ? prev.tiers.filter((t) => t !== tierValue) : [...prev.tiers, tierValue]
      };
    });
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    setHasSearched(true);
    await loadUsers();
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback('Copied');
      window.setTimeout(() => setCopyFeedback(''), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyFeedback('Copy failed');
      window.setTimeout(() => setCopyFeedback(''), 1500);
    }
  };

  const copyPreviewPackage = async () => {
    try {
      await navigator.clipboard.writeText(previewPackage.text);
      setPreviewCopyFeedback('Copied');
      window.setTimeout(() => setPreviewCopyFeedback(''), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
      setPreviewCopyFeedback('Copy failed');
      window.setTimeout(() => setPreviewCopyFeedback(''), 1500);
    }
  };

  const copyCreatedPackage = async () => {
    try {
      await navigator.clipboard.writeText(createdPackage.text);
      setCreatedCopyFeedback('Copied');
      window.setTimeout(() => setCreatedCopyFeedback(''), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
      setCreatedCopyFeedback('Copy failed');
      window.setTimeout(() => setCreatedCopyFeedback(''), 1500);
    }
  };

  const previewPackage = useMemo(() => buildOnboardingPackage(form, parents), [form, parents]);

  return (
    <LayoutV2>
      <PageHeader
        eyebrow="Admin"
        title="User Management"
        description="Search, create, edit, and disable accounts while keeping onboarding details visible for operations handoff."
        actions={
          <Button type="button" variant="primary" onClick={() => setShowForm((prev) => !prev)}>
            {showForm ? 'Cancel' : 'Add User'}
          </Button>
        }
      />

      <div className="mt-4 space-y-6">
        <SectionBlock eyebrow="Search" title="Filter users" description="Search by fuzzy keyword and tier without changing existing backend semantics.">
          <form onSubmit={handleSearchSubmit} className="space-y-4">
            <div>
              <label className="label">Name / Username</label>
              <input
                type="text"
                value={search.keyword}
                onChange={(e) => setSearch((prev) => ({ ...prev, keyword: e.target.value }))}
                placeholder="Fuzzy match by display name or username"
                className="input"
              />
            </div>
            <div>
              <p className="label">Tier</p>
              <div className="flex flex-wrap gap-2">
                {searchableTiers.map((tierOpt) => (
                  <button
                    key={tierOpt.value}
                    type="button"
                    onClick={() => toggleTier(tierOpt.value)}
                    className={`min-h-[44px] rounded-full border px-4 text-sm ${
                      search.tiers.includes(tierOpt.value)
                        ? 'border-blue-200/20 bg-blue-300/14 text-blue-100'
                        : 'border-white/10 bg-white/[0.03] text-slate-200'
                    }`}
                  >
                    {tierOpt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" variant="primary">Search</Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSearch({ keyword: '', tiers: [] });
                  setHasSearched(false);
                  setList([]);
                }}
              >
                Clear
              </Button>
            </div>
          </form>
        </SectionBlock>

        {showForm ? (
          <SectionBlock eyebrow="Create" title="Add user" description="Preview the onboarding package before creating the account.">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <input type="text" placeholder="Username" value={form.username} onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))} className="input" required />
              <div className="flex gap-2">
                <input type="text" placeholder="Password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} className="input font-mono" required />
                <Button type="button" variant="secondary" onClick={() => setForm((p) => ({ ...p, password: generatePreviewPassword() }))}>Regen</Button>
                <Button type="button" variant="ghost" onClick={() => copyText(form.password)}>Copy</Button>
              </div>
              {copyFeedback ? <p className="-mt-2 text-sm text-emerald-200 md:col-span-2">{copyFeedback}</p> : null}
              <input type="text" placeholder="Display Name" value={form.displayName} onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))} className="input" required />
              <select value={form.tier} onChange={(e) => setForm((p) => ({ ...p, tier: parseInt(e.target.value, 10), parentId: parseInt(e.target.value, 10) === 4 ? '' : p.parentId }))} className="input"><option value={4}>Operations</option><option value={3}>Promoter</option><option value={2}>Manager</option><option value={1}>Director</option></select>
              <select value={form.parentId} onChange={(e) => setForm((p) => ({ ...p, parentId: e.target.value }))} className="input"><option value="">No Parent</option>{form.tier === 4 ? null : parents.filter((p) => p.tier < form.tier && p.tier < 4).map((p) => <option key={p.id} value={p.id}>{p.display_name} ({tierNames[p.tier]})</option>)}</select>
              <input type="text" placeholder="Region" value={form.region} onChange={(e) => setForm((p) => ({ ...p, region: e.target.value }))} className="input" />
              <div className="rounded-[22px] border border-white/8 bg-slate-950/40 p-4 md:col-span-2">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-white">Onboarding Package Preview</p>
                   <div className="flex items-center gap-2">
                     <Button type="button" variant="ghost" onClick={copyPreviewPackage}>Copy Onboarding Info</Button>
                     {previewCopyFeedback && <span className="text-sm text-emerald-200">{previewCopyFeedback}</span>}
                   </div>
                </div>
                <pre className="whitespace-pre-wrap break-words text-xs text-slate-300">{previewPackage.text}</pre>
              </div>
              <div className="md:col-span-2">
                <Button type="submit" variant="primary">Create</Button>
              </div>
            </form>
          </SectionBlock>
        ) : null}

        {createdPackage ? (
          <SectionBlock eyebrow="Output" title="Created onboarding package">
             <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
               <pre className="whitespace-pre-wrap break-words text-xs text-slate-200">{createdPackage.text}</pre>
               <div className="flex items-center gap-2">
                 <Button type="button" variant="primary" onClick={copyCreatedPackage}>Copy</Button>
                 {createdCopyFeedback && <span className="text-sm text-emerald-200">{createdCopyFeedback}</span>}
               </div>
             </div>
          </SectionBlock>
        ) : null}

        <SectionBlock eyebrow="Results" title="User list" description="Open details, edit profile fields, or disable accounts from this condensed admin list.">
          {loading ? <Loading text="Loading users..." /> : !hasSearched ? (
            <div className="text-sm text-slate-400">Set filters and click Search to load users.</div>
          ) : list.length === 0 ? (
            <div className="text-sm text-slate-400">No users found.</div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {list.map((user) => (
                <div key={user.id} className="rounded-[20px] border border-white/8 bg-white/[0.03] p-4 text-sm">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <button onClick={() => navigate(`/admin/users/${user.id}`)} className="font-medium text-blue-200 hover:text-blue-100">
                        {user.display_name}
                      </button>
                      <p className="mt-1 text-slate-400">@{user.username} · {tierNames[user.tier]}</p>
                      <p className="mt-1 text-slate-400">Referral Code: <span className="font-mono text-slate-200">{user.inviteCode || '--'}</span></p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="secondary" onClick={() => openEdit(user)}>Edit</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => handleResetPassword(user.id)}>Set PW</Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={() => handleDisable(user.id)}
                        disabled={user.username === 'admin' || user.status === 'disabled'}
                      >
                        {user.status === 'disabled' ? 'Disabled' : 'Disable'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionBlock>

        {showEdit ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3">
            <form onSubmit={handleEdit} className="w-full max-w-md rounded-[28px] border border-white/10 bg-[#0f172a] p-5 space-y-3">
              <input type="text" value={editForm.username} onChange={(e) => setEditForm((p) => ({ ...p, username: e.target.value }))} className="input" required />
              <input type="text" value={editForm.displayName} onChange={(e) => setEditForm((p) => ({ ...p, displayName: e.target.value }))} className="input" required />
              <select value={editForm.tier} onChange={(e) => setEditForm((p) => ({ ...p, tier: parseInt(e.target.value, 10), parentId: parseInt(e.target.value, 10) === 4 ? '' : p.parentId }))} className="input"><option value={4}>Operations</option><option value={3}>Promoter</option><option value={2}>Manager</option><option value={1}>Director</option></select>
              <select value={editForm.parentId} onChange={(e) => setEditForm((p) => ({ ...p, parentId: e.target.value }))} className="input"><option value="">No Parent</option>{editForm.tier === 4 ? null : parents.filter((p) => p.tier < editForm.tier && p.tier < 4).map((p) => <option key={p.id} value={p.id}>{p.display_name} ({tierNames[p.tier]})</option>)}</select>
              <input type="text" value={editForm.region} onChange={(e) => setEditForm((p) => ({ ...p, region: e.target.value }))} className="input" />
              <select value={editForm.status} onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))} className="input"><option value="active">Active</option><option value="disabled">Disabled</option></select>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="ghost" className="flex-1" onClick={() => setShowEdit(null)}>Cancel</Button>
                <Button type="submit" variant="primary" className="flex-1">Save</Button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </LayoutV2>
  );
}
